import { supabase } from '@/integrations/supabase/client';

export interface Team {
  id: string;
  name: string;
  description?: string;
  budget?: number;
  median?: number;
  optimal?: number;
}

/** Organization settings backed by the teams table */
export interface OrganizationSettings {
  id: string;
  name: string;
  domain: string | null;
  logo_url: string | null;
  approval_mode: boolean;
  enable_quick_setup: boolean;
}

export type OrganizationSettingsUpdate = Omit<OrganizationSettings, 'id'>;

export interface Membership {
  id: string;
  user_id: string;
  team_id: string;
  is_admin: boolean;
  is_accepted: boolean;
  team: Team;
}

/**
 * Fetch all teams and memberships for a user
 */
export async function getUserMemberships(userId: string) {
  const { data, error } = await supabase
    .from('memberships')
    .select(`
      id,
      user_id,
      team_id,
      is_admin,
      is_accepted,
      teams:team_id (
        id,
        name,
        description,
        budget,
        median,
        optimal
      )
    `)
    .eq('user_id', userId)
    .eq('is_accepted', true);

  if (error) throw error;

  // Transform the data
  const memberships: Membership[] = (data || [])
    .filter(m => m.teams)
    .map(m => ({
      id: m.id,
      user_id: m.user_id,
      team_id: m.team_id,
      is_admin: m.is_admin,
      is_accepted: m.is_accepted,
      team: Array.isArray(m.teams) ? m.teams[0] : m.teams
    }));

  return memberships;
}

/**
 * Get a specific team by ID
 */
export async function getTeamById(teamId: string) {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('id', teamId)
    .single();

  if (error) throw error;
  return data as Team;
}

/**
 * Check if a user is admin of a team (from memberships array)
 */
export function isUserTeamAdmin(memberships: Membership[], teamId: string): boolean {
  const membership = memberships.find(m => m.team_id === teamId);
  return membership?.is_admin ?? false;
}

/**
 * Get all team members (profiles)
 */
export async function getTeamMembers(teamId: string) {
  const { data, error } = await supabase
    .from('memberships')
    .select(`
      id,
      user_id,
      is_admin,
      profiles:user_id (
        id,
        full_name,
        avatar_url,
        job_title
      )
    `)
    .eq('team_id', teamId)
    .eq('is_accepted', true);

  if (error) throw error;

  return (data || []).map(m => ({
    membershipId: m.id,
    userId: m.user_id,
    isAdmin: m.is_admin,
    profile: Array.isArray(m.profiles) ? m.profiles[0] : m.profiles
  }));
}

/**
 * Update user's active team in their profile
 */
export async function setActiveTeam(params: { userId: string; teamId: string }) {
  const { userId, teamId } = params;
  const { error } = await supabase
    .from('profiles')
    .update({ active_team_id: teamId })
    .eq('id', userId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Organization settings (team-backed)
// ---------------------------------------------------------------------------

/**
 * Fetch organization settings for the given team.
 */
export async function getOrganizationSettings(teamId: string): Promise<OrganizationSettings> {
  const { data, error } = await supabase
    .from('teams')
    .select('id, name, domain, logo_url, approval_mode, enable_quick_setup')
    .eq('id', teamId)
    .single();

  if (error) throw error;
  return data as OrganizationSettings;
}

/**
 * Persist organization settings updates back to the teams table.
 */
export async function updateOrganizationSettings(
  teamId: string,
  updates: Partial<OrganizationSettingsUpdate>
): Promise<void> {
  const { error } = await supabase
    .from('teams')
    .update(updates)
    .eq('id', teamId);

  if (error) throw error;
}

/** Max logo file size: 2 MB */
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Upload a logo file for the given team to the `org-logos` Supabase Storage
 * bucket and return the public URL.
 * Throws if the file exceeds 2 MB.
 */
export async function uploadOrganizationLogo(
  teamId: string,
  file: File
): Promise<string> {
  if (file.size > LOGO_MAX_BYTES) {
    throw new Error(`Logo must be smaller than 2 MB (received ${(file.size / 1024 / 1024).toFixed(2)} MB)`);
  }

  const ext = file.name.split('.').pop() ?? 'png';
  const path = `${teamId}/logo.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('org-logos')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from('org-logos').getPublicUrl(path);
  // Bust cache with a timestamp query param
  return `${data.publicUrl}?t=${Date.now()}`;
}

/**
 * Remove a user from a team by deleting their membership record.
 * This does NOT delete the user's profile – only the team membership row.
 */
export async function removeTeamMember(membershipId: string): Promise<void> {
  const { error } = await supabase
    .from('memberships')
    .delete()
    .eq('id', membershipId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------

/**
 * Get team by user ID (for backwards compatibility with old schema)
 * Returns the user's active team or first team
 */
export async function getTeamForUser(userId: string): Promise<string | null> {
  // First try to get active team from profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('active_team_id')
    .eq('id', userId)
    .single();

  if (profile?.active_team_id) {
    return profile.active_team_id;
  }

  // Otherwise get first membership
  const { data: membership } = await supabase
    .from('memberships')
    .select('team_id')
    .eq('user_id', userId)
    .eq('is_accepted', true)
    .limit(1)
    .maybeSingle();

  return membership?.team_id || null;
}
